import { enc, mode, AES } from 'crypto-js';
import { ref, type Ref } from 'vue';

interface UseCryptoReturn {
  result: Ref<string | undefined>;
  encrypt: Encrypt;
  decrypt: Decrypt;
}

type Encrypt = (path: string, hash: string, separator?: string) => Promise<void>;
type Decrypt = (path: string, hash: string, separator?: string) => Promise<void>;

/**
 * Fetches and parses a file to extract a cryptographic key and IV.
 * @param {string} path - The path to the file containing the key and IV.
 * @param {string} separator - The separator string used in the file.
 * @returns {Promise<{key: import('crypto-js').lib.WordArray, iv: import('crypto-js').lib.WordArray}>}
 * @private
 */
async function _getKeyAndIv(path: string, separator: string) {
  try {
    const data = await fetch(path);
    if (!data.ok) {
      throw new Error(`Failed to fetch key file: ${data.statusText}`);
    }
    const payload = await data.text();
    const fileData = payload.split(separator);

    if (fileData.length < 3) {
      throw new Error('Invalid key file format.');
    }

    const key = enc.Utf8.parse(fileData[1]);
    const iv = enc.Utf8.parse(fileData[2]);

    return { key, iv };
  } catch (e) {
    throw e as Error; // Re-throw the error to be caught by the calling function
  }
}

/**
 * @typedef {object} UseCryptoReturn
 * @property {Ref<string|null>} result - The reactive result of the last operation (encrypted or decrypted string).
 * @property {Encrypt} encrypt - Function to perform encryption.
 * @property {Decrypt} decrypt - Function to perform decryption.
 */

/**
 * A Vue composable for performing AES encryption and decryption.
 * It manages loading, error, and result states reactively.
 * @returns {UseCryptoReturn}
 */
export const useCrypto = (): UseCryptoReturn => {
  const result: Ref<string | undefined> = ref(undefined);

  /**
   * Encrypts a string using a key and IV fetched from a remote path.
   * Updates the reactive `result`, `isLoading`, and `error` properties.
   * @param {string} path - The path to the file containing the key and IV.
   * @param {string} textToEncrypt - The plaintext string to encrypt.
   * @param {string} [separator='separator'] - The separator used in the key file.
   */
  const encrypt: Encrypt = async (path: string, textToEncrypt: string, separator: string = 'separator') => {
    result.value = undefined;

    try {
      const { key, iv } = await _getKeyAndIv(path, separator);
      const encrypted = AES.encrypt(textToEncrypt, key, {
        iv,
        mode: mode.CBC,
      });
      result.value = encrypted.toString();
    } catch (error) {
      throw error as Error;
    }
  };

  /**
   * Decrypts a string using a key and IV fetched from a remote path.
   * Updates the reactive `result` property.
   * @param {string} path - The path to the file containing the key and IV.
   * @param {string} textToDecrypt - The base64-encoded cipher text to decrypt.
   * @param {string} [separator='separator'] - The separator used in the key file.
   */
  const decrypt: Decrypt = async (path: string, textToDecrypt: string, separator: string = 'separator') => {
    if (!textToDecrypt) {
      result.value = undefined;
      return;
    }

    result.value = undefined;

    try {
      const { key, iv } = await _getKeyAndIv(path, separator);
      const decrypted = AES.decrypt(textToDecrypt, key, { iv });
      result.value = decrypted.toString(enc.Utf8);
    } catch (error) {
      throw error as Error;
    }
  };

  return { result, encrypt, decrypt };
};
